import { OffboardingInitiatedBy } from "@/modules/offboarding/enums"
import { Briefcase, Mail, Calendar, User, FileText, CheckCircle, XCircle, Eye } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import type { OffboardingDto } from "@/modules/offboarding/dtos"

function getInitials(first: string, last: string) {
  return `${first.trim().slice(0, 1)}${last.trim().slice(0, 1)}`.toUpperCase()
}

function getStatusBadge(status: string) {
  const config: Record<string, string> = {
    pending:      "bg-yellow-100 text-yellow-800 border-yellow-200",
    accepted:     "bg-green-100 text-green-700 border-green-200",
    rejected:     "bg-red-100 text-red-700 border-red-200",
  }
  const cls = config[status.toLowerCase()] ?? "bg-muted text-foreground border-border"
  return <Badge className={cls}>{status}</Badge>
}

function getInitiatedByBadge(initiatedBy: string) {
  return initiatedBy?.toLowerCase() === OffboardingInitiatedBy.Admin.toLowerCase()
    ? <Badge className="bg-purple-100 text-purple-700 border-purple-200">Admin</Badge>
    : <Badge className="bg-blue-100 text-blue-700 border-blue-200">Employee</Badge>
}

interface Props {
  offboarding: OffboardingDto
}

export function OffboardingCard({ offboarding }: Props) {
  const navigate = useNavigate()
  const submittedCount = offboarding.documents.filter((d) => d.submitted).length
  const totalDocs = offboarding.documents.length

  return (
    <Card className="bg-card border border-border hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6">
          {/* Left: Avatar + Info */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-11 h-11 bg-muted rounded-full flex items-center justify-center shrink-0">
              <span className="text-sm font-semibold text-foreground">
                {getInitials(offboarding.employeeFirstName, offboarding.employeeLastName)}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-semibold text-lg text-foreground">
                  {offboarding.employeeFirstName} {offboarding.employeeLastName}
                </h3>
                {getStatusBadge(offboarding.resignationStatus)}
                {getInitiatedByBadge(offboarding.initiatedBy)}
              </div>

              <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{offboarding.employeeEmail}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{offboarding.employeePosition}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Last day: {new Date(offboarding.lastWorkingDate).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="mt-2 flex items-start gap-1 text-sm text-muted-foreground">
                <User className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="italic">{offboarding.offboardingReason}</span>
              </div>

              {offboarding.comments && (
                <p className="mt-1 text-sm text-muted-foreground italic">"{offboarding.comments}"</p>
              )}
            </div>
          </div>

          {/* Right: Documents + View */}
          <div className="shrink-0 flex flex-col items-end gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/onboarding/offboarding/${offboarding.id}`)}
              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </Button>
              {totalDocs > 0 && (
              <div className="min-w-[180px]">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" />
                  Documents ({submittedCount}/{totalDocs} submitted)
                </p>
                <div className="space-y-1">
                  {offboarding.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 text-sm">
                      {doc.submitted
                        ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      }
                      {doc.documentUrl ? (
                        <a
                          href={doc.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline truncate max-w-[140px]"
                        >
                          {doc.documentName}
                        </a>
                      ) : (
                        <span className="text-muted-foreground truncate max-w-[140px]">{doc.documentName}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
